import { BlueprintFormData } from '../types';
import { CalculationType, RentalSource } from './constants';
import { convertToCents } from 'utils/currencyUtils';
import { CurrencyCode } from 'utils/localization';

export const buildCalculations = (formData: BlueprintFormData) => {
  // Generator count for different calculations
  // Conventional uses gensetCount from step 2, Hybrid uses gensetRentalCount from step 3
  const conventionalGeneratorCount = Number(formData.gensetCount) || 0;
  const hybridGeneratorCount =
    formData.gensetRentalSource === RentalSource.NONE ? 0 : Number(formData.gensetRentalCount) || 0;

  // Generator fields based on rental source
  const generatorTypeId =
    formData.gensetRentalSource === RentalSource.CATALOG && formData.gensetType
      ? (() => {
          const num = Number(formData.gensetType);
          return isNaN(num) || num === 0 ? null : num;
        })()
      : null;

  // Generator capacity: Conventional uses gensetKva from step 2, Hybrid uses hybridGensetKva from step 3
  const conventionalGeneratorCapacityKva = formData.gensetKva
    ? (() => {
        const num = Number(formData.gensetKva);
        return isNaN(num) ? null : num;
      })()
    : null;
  const hybridGeneratorCapacityKva =
    formData.gensetRentalSource === RentalSource.MANUAL && formData.hybridGensetKva
      ? (() => {
          const num = Number(formData.hybridGensetKva);
          return isNaN(num) ? null : num;
        })()
      : null;

  // Battery fields based on rental source
  const batteryTypeId =
    formData.batteryRentalSource === RentalSource.CATALOG && formData.batteryModel
      ? (() => {
          const num = Number(formData.batteryModel);
          return isNaN(num) || num === 0 ? null : num;
        })()
      : null;
  const batterySizeKwh =
    formData.batteryRentalSource === RentalSource.MANUAL &&
    formData.converterPower &&
    Number(formData.converterPower) > 0
      ? (() => {
          const num = Number(formData.batterySize);
          return isNaN(num) ? null : num;
        })()
      : null;
  const batteryConverterPowerKw =
    formData.batteryRentalSource === RentalSource.MANUAL && formData.converterPower
      ? (() => {
          const num = Number(formData.converterPower);
          return isNaN(num) ? null : num;
        })()
      : null;

  return [
    {
      calculation_name: CalculationType.HYBRID_SOLUTION,
      is_base_calculation: false,
      currency_code: formData.currency.toLowerCase() as CurrencyCode,
      cost_assumptions: {
        diesel_price_per_liter_price_cents: convertToCents(formData.hybridDieselPricePerLiter || 0),
        energy_price_per_kwh_price_cents:
          formData.mainsConnection && formData.hybridElectricityPricePerKwh
            ? convertToCents(formData.hybridElectricityPricePerKwh)
            : 0,
        // Only include genset rental cost if generator count is greater than 0
        genset_rental_cost_cents: hybridGeneratorCount > 0 ? convertToCents(formData.gensetRentalCost || 0) : null,
        battery_rental_cost_cents: formData.batteryRentalCost ? convertToCents(formData.batteryRentalCost) : null,
        additional_costs_cents: formData.hybridAdditionalCostsPerWeek
          ? convertToCents(formData.hybridAdditionalCostsPerWeek)
          : null,
      },
      power_setup: {
        generator_count: hybridGeneratorCount,
        generator_capacity_kva: hybridGeneratorCapacityKva,
        generator_type_id: generatorTypeId,
        battery_count:
          formData.batteryRentalSource === RentalSource.CATALOG
            ? formData.batteryRentalCount || 0
            : formData.converterPower && Number(formData.converterPower) > 0
              ? formData.batteryRentalCount || 0
              : 0,
        battery_size_kwh: batterySizeKwh,
        battery_converter_power_kw: batteryConverterPowerKw,
        battery_type_id: batteryTypeId,
        mains_connection_max_amps: formData.mainsConnection
          ? formData.maxAmpsAvailable != null && formData.maxAmpsAvailable > 0
            ? formData.maxAmpsAvailable
            : null
          : null,
        power_source: 'h2',
      },
    },
    {
      calculation_name: CalculationType.CONVENTIONAL_SOLUTION,
      is_base_calculation: true,
      currency_code: formData.currency.toLowerCase() as CurrencyCode,
      cost_assumptions: {
        diesel_price_per_liter_price_cents: convertToCents(formData.conventionalDieselPricePerLiter || 0),
        energy_price_per_kwh_price_cents: 0,
        genset_rental_cost_cents: convertToCents(formData.conventionalGensetRentalCostPerWeek || 0),
        battery_rental_cost_cents: null,
        additional_costs_cents: formData.conventionalAdditionalCostsPerWeek
          ? convertToCents(formData.conventionalAdditionalCostsPerWeek)
          : null,
      },
      power_setup: {
        generator_count: conventionalGeneratorCount,
        generator_capacity_kva: conventionalGeneratorCapacityKva,
        generator_type_id: null,
        battery_count: 0,
        battery_size_kwh: null,
        battery_converter_power_kw: null,
        battery_type_id: null,
        mains_connection_max_amps: formData.mainsConnection
          ? formData.maxAmpsAvailable != null && formData.maxAmpsAvailable > 0
            ? formData.maxAmpsAvailable
            : null
          : null,
        power_source: 'h2',
      },
    },
  ];
};